import { routes } from "./routes.js"
import { createRouter, createWebHistory } from "vue-router"

import { useAuthStore } from "../stores/auth"
import * as Sentry from "@sentry/vue"

const router = createRouter({
    history: createWebHistory(`${import.meta.env.VITE_NEW_BASE_URL}`),
    routes,
})

const addMetaTags = (metaTags) => {
    const VUE_META_TAGS_ATTR = "data-vue-meta"

    // Remove tags that were added in other views
    Array.from(document.querySelectorAll(`[${VUE_META_TAGS_ATTR}]`)).map((el) =>
        el.parentNode.removeChild(el)
    )

    const metaTagsArray = Object.entries(metaTags || {})
    metaTagsArray
        .map(([metaTagKey, metaTagValue]) => {
            const tag = document.createElement("meta")
            tag.setAttribute(metaTagKey, metaTagValue)
            tag.setAttribute(VUE_META_TAGS_ATTR, "")

            return tag
        }) // Add the meta tags to the document head.
        .forEach((tag) => document.head.appendChild(tag))
}

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()

    addMetaTags(to.meta.metaTags)

    // Retrieve user if not there (NOTE: the store is cleared on page refresh)
    if (!authStore.isLoggedIn) {
        try {
            await authStore.retrieveUser()
        } catch (error) {
            return to.name === "login" ? next() : next({ name: "login" })
        }

        await authStore.getCsrfToken()
        try {
            await authStore.getCurrentOrganization()
        } catch (error) {
            console.log(error)
        }
    }

    // CHECK USER REGISTRATION IS COMPLETE
    if (!authStore.isRegistrationComplete && to.name !== "register") {
        // If the user hasn't completed registration or does not belong to any project,
        //  then redirect to registration view
        return next({ name: "register" })
    } else if (authStore.isRegistrationComplete && to.name === "register") {
        // If the user tries to access registration but is already registered, redirect
        // them home.
        return next({ name: "home" })
    }

    if (to.meta.requiresAuth) {
        // Assertions
        if (!authStore.currentUser)
            throw "User must be already specified at this point"

        // Add user/organization details to sentry errors
        Sentry.setUser({
            email: authStore.currentUser.email,
            id: authStore.currentUser.id,
        })
        if (authStore.currentOrganization) {
            Sentry.setTags({
                organization: authStore.currentOrganization.name,
                organization_id: authStore.currentOrganization.id,
            })
        }

        // Some pages are superadmin-only. Redirect other users home.
        if (to.meta.requiresSuperAdmin && !authStore.isSuperAdmin)
            next({ name: "home" })

        // Else go to the requested view
        next()
    } else if (to.meta.requiresAnonymous && authStore.isLoggedIn) {
        // Some routes, e.g. login, require the user not to be logged in. If
        //  a logged in user tries to acess them, redirect to the home page.
        next({ name: "home" })
    } else next()
})

export default router
